import {
  AppShell,
  Badge,
  Box,
  Burger,
  Group,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconStar } from '@tabler/icons-react';
import { useMemo, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSessionActor, useStaffProfile } from './profile-context.tsx';
import { tokens } from './ui/tokens.ts';
import { API_WORKER_URL, venueSlugFromBrowserHost } from '@shared-endpoints';

async function handleLogout(): Promise<void> {
  try {
    const apiBase =
      import.meta.env.VITE_API_BASE_URL ??
      (import.meta.env.PROD ? API_WORKER_URL : '');
    const slug = venueSlugFromBrowserHost({
      devFallback: import.meta.env.VITE_DEV_VENUE_SLUG,
    });
    await fetch(`${apiBase}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: slug ? { 'x-ubc-venue-slug': slug } : {},
    });
  } catch {
    // Ignore — on error we still reload and let the session/cookie
    // state reconcile server-side.
  }
  window.location.reload();
}
import { buildNavTree, filterVisibleTree, ROLE_BADGE } from './ui/sidebar/nav-tree.tsx';
import { Sidebar } from './ui/sidebar/Sidebar.tsx';
import { useSidebarFoldState } from './ui/sidebar/use-sidebar-fold-state.ts';

/**
 * Layout shell — wires header + sidebar + main. Single responsibility:
 * compose AppShell. Nav tree / fold state / persistence live in
 * `./ui/sidebar/*`.
 *
 * Mobile (sm breakpoint, ~640px and below):
 *   - Sidebar collapses; Header shows a Burger control
 *   - Burger toggles AppShell's mobile drawer (built-in, slides in)
 *   - Tapping a nav leaf auto-closes the drawer (better than leaving
 *     it open over the page user just navigated to)
 */
export function Layout({ children }: { children?: ReactNode } = {}): JSX.Element {
  const theme = useMantineTheme();
  const profile = useStaffProfile();
  const session = useSessionActor();
  const location = useLocation();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);

  const tree = useMemo(
    () => buildNavTree(profile.capabilities.notifications.enabled),
    [profile.capabilities.notifications.enabled],
  );
  const visibleTree = useMemo(
    () => filterVisibleTree(tree, session.role),
    [tree, session.role],
  );
  const fold = useSidebarFoldState(tree, location.pathname);

  return (
    <Box style={{ minHeight: '100vh', background: theme.other['pageBackground'] as string }}>
      <AppShell
        header={{ height: 64 }}
        navbar={{
          width: 240,
          breakpoint: 'sm',
          collapsed: { mobile: !mobileOpened },
        }}
        padding="md"
      >
        <AppShell.Header
          style={{
            background: tokens.color.headerSurface,
            backdropFilter: 'blur(8px)',
            borderBottom: tokens.color.headerBorder,
          }}
        >
          <Group h="100%" px="lg" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Burger
                opened={mobileOpened}
                onClick={toggleMobile}
                hiddenFrom="sm"
                size="md"
                color={tokens.color.primary}
                aria-label="開合側邊選單"
              />
              <IconStar size={22} color={tokens.color.primary} />
              <Stack gap={0}>
                <Title order={4} c="navy.8" style={{ lineHeight: 1.1 }}>
                  {profile.branding.appTitle}
                </Title>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1 }} visibleFrom="xs">
                  店家後台
                </Text>
              </Stack>
            </Group>
            <Group gap="xs">
              <Badge
                variant="filled"
                color={ROLE_BADGE[session.role].color}
                size="sm"
                radius="sm"
                aria-label={session.role}
              >
                {ROLE_BADGE[session.role].label}
              </Badge>
              {session.actorId !== 'dev-fallback' && session.actorId !== 'system' && (
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: tokens.color.mantine['neutral'],
                    fontSize: 12,
                    cursor: 'pointer',
                    padding: '4px 8px',
                  }}
                  aria-label="登出"
                >
                  登出
                </button>
              )}
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          p="md"
          style={{
            background: tokens.color.navbarBackground,
            borderRight: tokens.color.navbarBorder,
          }}
        >
          <Sidebar
            tree={visibleTree}
            fold={fold}
            currentPath={location.pathname}
            onNavigate={closeMobile}
          />
        </AppShell.Navbar>

        <AppShell.Main>{children ?? <Outlet />}</AppShell.Main>
      </AppShell>
    </Box>
  );
}
